import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { PersonAdd as PersonAddIcon } from "@mui/icons-material";
import { postAdminUserCreate } from "../services/callApi";
import {
  getApiStatus,
  getManagementApiErrorMessage,
} from "../utils/managementApiError";
import {
  buildAdminUserCreatePayload,
  generateIdempotencyKey,
  isAdminUserCreateRetryable,
  validateAdminUserCreateForm,
} from "../utils/adminUserCreate";

const INITIAL_FORM_DATA = {
  cognitoUsername: "",
  email: "",
  adminUserName: "",
  role: "admin",
  companyId: "",
};

function AdminUserCreate({ companies, onBack, onCreated, onForbidden }) {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRetry, setIsRetry] = useState(false);
  const currentRequestRef = useRef(null);
  const submitInFlightRef = useRef(false);

  const resetRequest = () => {
    currentRequestRef.current = null;
    setIsRetry(false);
    setInfoMessage("");
  };

  const handleChange = (name) => (event) => {
    const value = event.target.value;
    setFormData((current) => ({ ...current, [name]: value }));
    setErrorMessage("");
    resetRequest();
  };

  const handleRoleChange = (event) => {
    const role = event.target.value;
    setFormData((current) => ({
      ...current,
      role,
      companyId: role === "super_admin" ? "" : current.companyId,
    }));
    setErrorMessage("");
    resetRequest();
  };

  const handleApiError = (error) => {
    const status = getApiStatus(error);
    if (status === 401) {
      window.location.href = "/login";
      return;
    }
    if (status === 403) {
      onForbidden("adminUserList");
      return;
    }
    setErrorMessage(getManagementApiErrorMessage(error));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setInfoMessage("");

    const validationMessage = validateAdminUserCreateForm(formData);
    if (validationMessage) {
      setErrorMessage(validationMessage);
      return;
    }

    if (submitInFlightRef.current) {
      return;
    }

    const payload = buildAdminUserCreatePayload(formData);
    const payloadSignature = JSON.stringify(payload);
    let currentRequest = currentRequestRef.current;

    // 同じ登録内容の再試行では、最初に発行した冪等キーを再利用する。
    if (!currentRequest || currentRequest.payloadSignature !== payloadSignature) {
      currentRequest = {
        idempotencyKey: generateIdempotencyKey(),
        payloadSignature,
      };
      currentRequestRef.current = currentRequest;
    }

    submitInFlightRef.current = true;
    setIsSubmitting(true);
    try {
      const response = await postAdminUserCreate(
        payload,
        currentRequest.idempotencyKey
      );

      if (response?.status === 202) {
        setInfoMessage(
          "登録処理中です。しばらくしてから同じ内容で再試行してください。"
        );
        setIsRetry(true);
        submitInFlightRef.current = false;
        setIsSubmitting(false);
        return;
      }

      if (response?.status !== 201) {
        currentRequestRef.current = null;
        setIsRetry(false);
        setErrorMessage(
          response?.data?.message ?? "管理者の登録に失敗しました。"
        );
        submitInFlightRef.current = false;
        setIsSubmitting(false);
        return;
      }

      currentRequestRef.current = null;
      onCreated(response.data);
    } catch (error) {
      const status = getApiStatus(error);
      if (isAdminUserCreateRetryable(status)) {
        setIsRetry(true);
      } else {
        currentRequestRef.current = null;
        setIsRetry(false);
      }
      handleApiError(error);
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 1 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="h5" component="h1">
          管理者新規登録
        </Typography>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: "pointer" }}
            onClick={(event) => {
              event.preventDefault();
              onBack();
            }}
          >
            管理者一覧
          </Link>
          <Typography color="text.primary">新規登録</Typography>
        </Breadcrumbs>
      </Box>

      {infoMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {infoMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 } }} elevation={3}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            required
            label="ログインID"
            value={formData.cognitoUsername}
            onChange={handleChange("cognitoUsername")}
            inputProps={{ maxLength: 128 }}
            disabled={isSubmitting}
            autoComplete="username"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            required
            type="email"
            label="メールアドレス"
            value={formData.email}
            onChange={handleChange("email")}
            disabled={isSubmitting}
            autoComplete="email"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            required
            label="氏名"
            value={formData.adminUserName}
            onChange={handleChange("adminUserName")}
            disabled={isSubmitting}
            autoComplete="name"
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth required sx={{ mb: 2 }} disabled={isSubmitting}>
            <InputLabel id="admin-user-create-role-label">権限</InputLabel>
            <Select
              labelId="admin-user-create-role-label"
              label="権限"
              value={formData.role}
              onChange={handleRoleChange}
            >
              <MenuItem value="admin">admin</MenuItem>
              <MenuItem value="super_admin">super_admin</MenuItem>
            </Select>
          </FormControl>
          <FormControl
            fullWidth
            required={formData.role === "admin"}
            sx={{ mb: 3 }}
            disabled={isSubmitting || formData.role === "super_admin"}
          >
            <InputLabel id="admin-user-create-company-label">所属会社</InputLabel>
            <Select
              labelId="admin-user-create-company-label"
              label="所属会社"
              value={formData.companyId}
              onChange={handleChange("companyId")}
            >
              {companies.map((company) => (
                <MenuItem key={company.companyId} value={company.companyId}>
                  {company.companyName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ textAlign: "center" }}>
            <Button
              variant="contained"
              type="submit"
              startIcon={<PersonAddIcon />}
              disabled={isSubmitting}
            >
              {isRetry ? "再試行" : "登録"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default AdminUserCreate;
